import { useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { strings } from '../strings';
import { useDock } from './context';
import { clampSize, GEOMETRY, minSize, maxSize, type DockZone } from './layout';
import { zoneId } from './ids';

/**
 * The resize handle of a dock zone (spec 6.8.6, 6.8.7): `role="separator"` with its size in pixels; arrow keys
 * resize by 16 px (Shift: 64 px), Enter collapses or expands the zone, dragging resizes within the limits.
 */
export function Splitter({ zone, size, otherSide }: { zone: DockZone; size: number; otherSide: number }) {
  const dock = useDock();
  const collapsed = dock.state.layout.zones[zone].collapsed;
  const drag = useRef<{ start: number; size: number } | null>(null);
  const vertical = zone !== 'bottom';
  const max = maxSize(zone, dock.workspace, otherSide);
  const min = Math.min(minSize(zone), max);

  const resizeTo = (next: number) => {
    if (collapsed) dock.collapse(zone, false);
    dock.resize(zone, clampSize(zone, next, dock.workspace, otherSide));
  };

  // Moving towards the page grows the zone.
  const grow = (delta: number) => (zone === 'right' || zone === 'bottom' ? -delta : delta);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? GEOMETRY.bigStep : GEOMETRY.step;
    const key = event.key;
    if (key === 'Enter') {
      event.preventDefault();
      dock.collapse(zone, !collapsed);
      return;
    }
    const delta = vertical
      ? key === 'ArrowLeft'
        ? -step
        : key === 'ArrowRight'
          ? step
          : 0
      : key === 'ArrowUp'
        ? -step
        : key === 'ArrowDown'
          ? step
          : 0;
    if (key === 'Home' || key === 'End') {
      event.preventDefault();
      resizeTo(key === 'Home' ? min : max);
      return;
    }
    if (delta === 0) return;
    event.preventDefault();
    resizeTo(size + grow(delta));
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const target = event.currentTarget;
    target.focus();
    drag.current = { start: vertical ? event.clientX : event.clientY, size };
    const onMove = (move: globalThis.PointerEvent) => {
      if (!drag.current) return;
      const delta = (vertical ? move.clientX : move.clientY) - drag.current.start;
      resizeTo(drag.current.size + grow(delta));
    };
    const onUp = () => {
      drag.current = null;
      document.body.classList.remove('dock-resizing');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    document.body.classList.add('dock-resizing');
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-valuenow={collapsed ? min : size}
      aria-valuetext={collapsed ? strings.dock.collapsed : `${size} px`}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-controls={zoneId(zone)}
      aria-label={strings.dock.resize(strings.dock.zone[zone])}
      data-splitter={zone}
      className={`dock-splitter ${vertical ? 'dock-splitter-v' : 'dock-splitter-h'}`}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
    />
  );
}
